import {VFC} from 'react';
import {useQuery} from 'services/apollo';
import {useParams, useHistory} from 'react-router-dom';
import {DriverAssignment} from 'services/graphql';
import {formatOrderNumber} from 'util/StringUtils';
import LoadingSpinner from 'components/LoadingSpinner';
import NotFound from 'components/NotFound';
import {EzAlert, EzBlankState, EzLayout, EzLink} from '@ezcater/recipe';
import PhoneNumbers from 'constants/PhoneNumbers';
import {FlashMessages} from '@ezcater/flash-messages';
import {TRACK_DRIVER_ASSIGNMENT_PAGE_NAME} from 'util/tracking/trackingEvents';
import {useDriverPageViewTracking} from 'util/tracking';
import {useTranslation} from 'react-i18next';
import {DELIVERIES_PATH} from 'paths';
import {useCurrentAccount} from 'components/CurrentAccount';
import {DmDeliveryStatus} from 'services/graphql/types';
import type {DriverAssignmentQuery, DriverAssignmentQueryVariables} from 'services/graphql/types';
import FeedbackForm from 'components/FeedbackForm';
import {StickyFooter} from 'components/FeedbackForm/FeedbackForm.styles';
import Layout from '../Layout';
import {
  ContentsContainer,
  ScrollBarContainer,
  ScrollBarContent,
  SummaryBarTitleContainer,
  DeliveryCompletedAlertWrapper,
} from './DriverProgress.styles';
import ActionButton from './ActionButton';
import StartDelivery from './StartDelivery';
import InProgress from './InProgress';

const NotFoundPage: VFC = () => {
  const {t} = useTranslation();
  return (
    <NotFound title={t('driverProgress.notFound.title')}>
      {t('driverProgress.notFound.message')}
      <EzLink href={`tel:${PhoneNumbers.EZDISPATCH_DIRECT}`}>
        {PhoneNumbers.EZDISPATCH_DIRECT}
      </EzLink>
      .
    </NotFound>
  );
};

const DriverProgress = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const {driverAssignmentId} = useParams<{driverAssignmentId: string}>();
  const currentAccount = useCurrentAccount();
  const currentAccountId = currentAccount.id || undefined;
  const {data, loading, error} = useQuery<DriverAssignmentQuery, DriverAssignmentQueryVariables>(
    DriverAssignment,
    {
      variables: {id: driverAssignmentId},
      onCompleted: queryResultData => {
        const status = queryResultData?.driverAssignment.delivery.status;

        if (status === DmDeliveryStatus.Expired) {
          history.replace(DELIVERIES_PATH);
        }
      },
    }
  );
  const assignment = data?.driverAssignment;

  useDriverPageViewTracking(
    TRACK_DRIVER_ASSIGNMENT_PAGE_NAME,
    assignment?.delivery?.id,
    currentAccountId
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !assignment) {
    return <NotFoundPage />;
  }

  const orderNumber = formatOrderNumber(assignment.delivery.orderNumber);
  const deliveryStatus = assignment.delivery.status || DmDeliveryStatus.Assigned;

  return (
    <Layout title={`${orderNumber} - ezCater Delivery`} orderNumber={orderNumber}>
      <ContentsContainer>
        <FlashMessages />
        <ScrollBarContainer>
          {orderNumber && (
            <SummaryBarTitleContainer>
              {t('driverProgress.header.orderNumber', {orderNumber})}
            </SummaryBarTitleContainer>
          )}
          <ScrollBarContent>
            {deliveryStatus === DmDeliveryStatus.Assigned && <StartDelivery data={data} />}
            {deliveryStatus === DmDeliveryStatus.InProgress && <InProgress data={data} />}
            {deliveryStatus === DmDeliveryStatus.Completed && (
              <EzLayout layout="stack">
                <DeliveryCompletedAlertWrapper>
                  <EzAlert use="success" headline="Delivery Completed" />
                </DeliveryCompletedAlertWrapper>
                <FeedbackForm driverAssignmentId={driverAssignmentId} />
              </EzLayout>
            )}
            {!!assignment.feedback && (
              <EzLayout layout="stack">
                <EzBlankState title="Feedback received" message="Thank you for your feedback" />
              </EzLayout>
            )}
          </ScrollBarContent>
          <StickyFooter>
            <ActionButton
              eventAt={assignment.delivery.eventAt}
              driverAssignmentId={driverAssignmentId}
              status={deliveryStatus}
            />
          </StickyFooter>
        </ScrollBarContainer>
      </ContentsContainer>
    </Layout>
  );
};

export default DriverProgress;
